import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, Download, Share, PlusSquare, X, LogOut } from 'lucide-react';
import { usePWA } from '@/providers/pwa-provider';
import { useAuthStore, Role } from '@/store/auth';
import logo from '@/assets/images/lincpay-logo.png';
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { AnimatePresence, motion } from 'framer-motion';

export default function Navbar() {
    const { isInstallable, isStandalone, installApp, showIOSGuide, dismissIOSGuide } = usePWA();
    const { currentUser, logout } = useAuthStore();

    return (
        <>
            <header className="sticky top-0 z-[1000] w-full navbar-glass border-b border-border">
                <div className="container flex h-16 items-center justify-between px-6">
                    <div className="flex items-center gap-2">
                        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity group">
                            <img
                                src={logo}
                                alt="LincPay Logo"
                                className="h-8 w-auto object-contain"
                            />
                            <div className="h-4 w-px bg-border hidden sm:block mx-1" />
                            <span className="hidden sm:block text-[10px] font-bold uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">Digital Ecosystem</span>
                        </Link>
                    </div>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center gap-8">
                        <Link to="/" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-primary transition-all relative group py-1" onClick={() => {
                            if (window.location.pathname === '/') {
                                document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' });
                            }
                        }}>
                            Home
                            <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-primary transition-all group-hover:w-full"></span>
                        </Link>
                        <Link to="/#products" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-primary transition-all relative group py-1" onClick={() => {
                            if (window.location.pathname === '/') {
                                document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
                            }
                        }}>
                            Products
                            <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-primary transition-all group-hover:w-full"></span>
                        </Link>
                        {currentUser?.role === Role.ADMIN && (
                            <Link to="/admin" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-primary transition-all relative group py-1">
                                Admin Suite
                                <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-primary transition-all group-hover:w-full"></span>
                            </Link>
                        )}
                        <Link to="/book-appointment" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-primary transition-all relative group py-1">
                            Book Demo
                            <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-primary transition-all group-hover:w-full"></span>
                        </Link>
                    </nav>

                    <div className="flex items-center gap-4">
                        {isInstallable && !isStandalone && (
                            <Button
                                onClick={installApp}
                                size="sm"
                                className="flex gap-2 bg-primary text-primary-foreground hover:opacity-90 text-xs px-3 font-semibold rounded-full"
                            >
                                <Download className="h-3.5 w-3.5" />
                                <span>Install App</span>
                            </Button>
                        )}

                        {currentUser && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={logout}
                                className="hidden md:flex rounded-full px-4 border-destructive/20 text-destructive hover:bg-destructive/10 h-8 text-xs font-semibold shadow-none transition-colors"
                            >
                                <LogOut className="h-3.5 w-3.5 mr-1.5" />
                                Logout
                            </Button>
                        )}

                        {/* Mobile Nav */}
                        <Sheet>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" className="md:hidden">
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-[300px] bg-card border-l border-border">
                                <SheetHeader className="text-left">
                                    <SheetTitle className="text-xl font-bold text-foreground">Menu</SheetTitle>
                                </SheetHeader>
                                <div className="flex flex-col gap-4 mt-8">
                                    <Link to="/" className="text-sm font-semibold text-muted-foreground hover:text-primary transition-colors px-2 py-1" onClick={() => {
                                        if (window.location.pathname === '/') {
                                            document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }}>
                                        Home
                                    </Link>
                                    <Link to="/#products" className="text-sm font-semibold text-muted-foreground hover:text-primary transition-colors px-2 py-1" onClick={() => {
                                        if (window.location.pathname === '/') {
                                            document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }}>
                                        Products
                                    </Link>
                                    {currentUser?.role === Role.ADMIN && (
                                        <Link
                                            to="/admin"
                                            className="text-sm font-semibold text-muted-foreground hover:text-primary transition-colors px-2 py-1"
                                        >
                                            Admin Suite
                                        </Link>
                                    )}
                                    <Link
                                        to="/book-appointment"
                                        className="text-sm font-semibold text-muted-foreground hover:text-primary transition-colors px-2 py-1"
                                    >
                                        Book Demo
                                    </Link>
                                    <div className="pt-6 border-t border-border mt-2 flex flex-col gap-3">
                                        {isInstallable && !isStandalone && (
                                            <Button onClick={installApp} className="w-full gap-2 bg-primary text-primary-foreground hover:opacity-90 rounded-full h-11 text-[10px] font-black uppercase tracking-widest shadow-none">
                                                <Download className="h-3.5 w-3.5" />
                                                Install Identity App
                                            </Button>
                                        )}
                                        {currentUser && (
                                            <Button
                                                variant="ghost"
                                                onClick={logout}
                                                className="w-full h-11 rounded-full border border-destructive/10 text-destructive/80 hover:bg-destructive/5 text-[10px] font-black uppercase tracking-widest gap-3 justify-center"
                                            >
                                                <LogOut className="h-4 w-4" />
                                                Terminate Session
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </header>

            {/* iOS / Safari Install Guide Overlay */}
            <AnimatePresence>
                {showIOSGuide && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                        onClick={dismissIOSGuide}
                    >
                        <motion.div
                            initial={{ y: 80, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 80, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="bg-card rounded-3xl shadow-2xl max-w-sm w-full p-8 relative border border-border"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={dismissIOSGuide}
                                className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-muted transition-colors"
                            >
                                <X className="h-5 w-5 text-muted-foreground" />
                            </button>

                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Download className="h-8 w-8 text-primary" />
                                </div>
                                <h3 className="text-xl font-bold mb-2 text-foreground">Install LincPay</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    Add this app to your home screen for the best experience.
                                </p>
                            </div>

                            <div className="space-y-5">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                        <span className="text-lg font-bold text-primary">1</span>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-sm mb-0.5 text-foreground">Tap the ••• icon</p>
                                        <p className="text-xs text-muted-foreground">
                                            Tap the three dots <span className="inline-flex items-center justify-center w-5 h-5 bg-muted rounded text-[10px] font-bold">•••</span> in the bottom toolbar of Safari
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                        <span className="text-lg font-bold text-primary">2</span>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-sm mb-0.5 text-foreground">Tap the Share button</p>
                                        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                            Look for the <Share className="h-3.5 w-3.5 inline" /> Share icon in the menu
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                        <span className="text-lg font-bold text-primary">3</span>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-sm mb-0.5 text-foreground">Add to Home Screen</p>
                                        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                            Scroll down and tap <PlusSquare className="h-3.5 w-3.5 inline" /> "Add to Home Screen", then tap "Add"
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <Button
                                onClick={dismissIOSGuide}
                                className="w-full mt-8 rounded-full h-12 font-bold text-base bg-primary text-primary-foreground hover:opacity-90"
                            >
                                Got it
                            </Button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
